import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import Forms from '../../../components/Forms';
import Error from '../../../components/Error';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';

const slugify = (s) =>
    s.toString()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

const LyricsCategoryModal = ({close, onCategoryUpdate, category}) => {
    const {t} = useTranslation();
    const inputRef = useRef(null);

    const isEdit = Boolean(category?._id);

    const [name, setName] = useState(category?.name || '');
    const [slug, setSlug] = useState(category?.slug || '');
    const [slugDirty, setSlugDirty] = useState(Boolean(category?.slug)); // ⬅️ sledujeme ruční zásah
    const [error, setError] = useState('');

    useEffect(() => {
        setTimeout(() => inputRef.current?.focus(), 1);
    }, []);

    // Když měníš název a slug nebyl ručně upraven, generuj automaticky
    useEffect(() => {
        if (!slugDirty) {
            setSlug(slugify(name));
        }
    }, [name, slugDirty]);

    // Při změně "category" (otevření/načtení jiného záznamu) vše resetuj konzistentně
    useEffect(() => {
        setName(category?.name || '');
        setSlug(category?.slug || '');
        setSlugDirty(Boolean(category?.slug)); // ⬅️ pokud má slug, považuj ho za ručně daný
        setError('');
    }, [category]);

    const errors = useMemo(() => {
        const e = {};
        if (!name.trim()) e.name = t('validation.required');
        if (slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) e.slug = t('validation.invalidSlug');
        return e;
    }, [name, slug, t]);

    // Uživatel mění slug ručně → označ jako "dirty".
    const handleSlugChange = (val) => {
        setSlug(val);
        setSlugDirty(val.length > 0 ? true : false); // když pole vymaže, umožníme znovu auto-generovat
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        // if (Object.keys(errors).length) return;

        const payload = {
            name: name.trim(),
            ...(slug ? {slug: slug.trim()} : {})
        };

        try {
            if (isEdit) {
                await API.put(API_ENDPOINTS.lyricsCategories.update(category._id), payload);
            } else {
                await API.post(API_ENDPOINTS.lyricsCategories.create, payload);
            }
            onCategoryUpdate?.();
            close();
        } catch (err) {
            setError(err);
            console.error('Failed to upsert lyrics category:', err);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Typography variant="h6" component="h2" className="mb-2">
                {isEdit ? t('lyricsCategories.edit') : t('lyricsCategories.add')}
            </Typography>

            <div className="grid grid-cols-2 gap-4 mb-4 items-start">
                <Forms.Text
                    ref={inputRef}
                    required
                    label={t('lyricsCategories.name')}
                    placeholder={t('lyricsCategories.name')}
                    value={name}
                    onChange={setName}
                    error={errors.name}
                    className="rounded-md col-span-2"
                />

                <Forms.Text
                    label={t('lyricsCategories.slug')}
                    placeholder={t('lyricsCategories.slug')}
                    value={slug}
                    onChange={handleSlugChange}
                    error={errors.slug}
                    className="rounded-md col-span-2"
                />
            </div>

            {error && <Error error={error}/>}

            <Forms.Button submit label={t('save')} className="mt-4"/>
        </form>
    );
};

export default LyricsCategoryModal;
